import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/api';
import { confirmJobStart, confirmJobCompletion } from '../api/jobs';
import { useAuth } from '../context/AuthContext';
import loadImage from '../assets/imageLoader';
import RatingModal from '../components/RatingModal';
import { 
    FaArrowLeft, FaMapMarkerAlt, FaMoneyBillWave, FaClock, FaUserTie, 
    FaPlay, FaCheckCircle, FaFlagCheckered, FaExclamationCircle, FaUser, FaHistory, FaMap, FaStar
} from 'react-icons/fa';

const JobManagementPage = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Rating Modal State
    const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
    const [ratingTargets, setRatingTargets] = useState([]); // Now an array of workers

    const fetchJobDetails = useCallback(async () => {
        try {
            const response = await apiClient.get(`/jobs/${jobId}`);
            setJob(response.data);
        } catch (err) {
            setError('Failed to load management dashboard.');
        } finally {
            setLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        fetchJobDetails();
    }, [fetchJobDetails]);

    const handleConfirmStart = async (contractId) => {
        if (!window.confirm("Confirm that this worker has started?")) return;
        try {
            // NOTE: The backend endpoint might need the jobId or contractId. 
            // In our current routes, confirmJobStart takes jobId. 
            // If multiple workers, we might need a contract-specific endpoint.
            await apiClient.post(`/jobs/${jobId}/confirm-start`); 
            fetchJobDetails();
        } catch (err) {
            alert("Failed to confirm start.");
        }
    };

    const handleConfirmCompletion = async (contractId) => {
        if (!window.confirm("Release payment to this worker?")) return;
        try {
            await apiClient.post(`/jobs/${jobId}/confirm-completion`);
            fetchJobDetails();
        } catch (err) {
            alert("Failed to confirm completion.");
        }
    };

    if (loading) return <div className="flex justify-center items-center h-screen bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div></div>;
    if (error) return <div className="p-10 text-center text-red-500 font-bold">{error}</div>;
    if (!job) return <div className="text-center mt-10">Job not found.</div>;

    const details = job.details?.[0] || {};
    const hiredWorkers = job.direct_hires?.filter(h => h.worker_id) || [];
    const applications = details.applications || [];

    return (
        <div className="min-h-screen bg-slate-50 font-sans pb-20">
            
            {/* Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 py-8">
                    <Link to="/owner-dashboard" className="inline-flex items-center text-slate-400 hover:text-emerald-600 font-bold text-sm mb-6 transition-colors">
                        <FaArrowLeft className="mr-2" /> Back to Workspace
                    </Link>
                    
                    <div className="flex flex-col md:flex-row justify-between items-start gap-6">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <h1 className="text-3xl font-black text-slate-800">{job.title}</h1>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-black uppercase tracking-widest">{job.status}</span>
                            </div>
                            <p className="text-slate-500 font-medium flex items-center gap-2">
                                <FaMapMarkerAlt className="text-emerald-500" /> 
                                {job.location?.address || 'Location Specified'}
                            </p>
                        </div>
                        
                        <div className="flex gap-4">
                            {hiredWorkers.some(h => h.status === 'completed' && !h.rating) && hiredWorkers.filter(h => h.status === 'completed' && !h.rating).length > 1 && (
                                <button 
                                    onClick={() => {
                                        const unrated = hiredWorkers.filter(h => h.status === 'completed' && !h.rating);
                                        setRatingTargets(unrated.map(h => ({ 
                                            workerId: h.hired_worker?.id, 
                                            workerName: h.hired_worker?.name, 
                                            hireId: h.id,
                                            type: 'Direct_Hire'
                                        })));
                                        setIsRatingModalOpen(true);
                                    }}
                                    className="bg-yellow-500 text-white px-6 py-3 rounded-xl font-black shadow-lg shadow-yellow-200 flex items-center gap-2 hover:bg-yellow-600 transition-all"
                                >
                                    <FaStar /> Rate All Team
                                </button>
                            )}
                            <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100 text-right">
                                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Total Job Budget</p>
                                <p className="text-2xl font-black text-emerald-700">৳{(details.budget_per_worker * details.required_workers).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* Left: Management Sections */}
                <div className="lg:col-span-2 space-y-8">
                    
                    {/* Hired Workers (The primary focus) */}
                    <div className="space-y-4">
                        <h2 className="text-lg font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                            <FaUserTie className="text-emerald-500" /> Hired Professionals ({hiredWorkers.length})
                        </h2>
                        
                        {hiredWorkers.length > 0 ? (
                            hiredWorkers.map(hire => (
                                <div key={hire.id} className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-6">
                                    <div className="flex items-center gap-4">
                                        <div className="relative">
                                            <img src={loadImage(hire.hired_worker?.picture)} className="w-16 h-16 rounded-2xl object-cover border-2 border-white shadow-md" alt="Worker" />
                                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                                        </div>
                                        <div>
                                            <Link to={`/workers/profile/${hire.hired_worker?.id}`} className="font-black text-slate-800 text-lg hover:text-emerald-600 transition-colors">{hire.hired_worker?.name}</Link>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${
                                                    hire.status === 'active' ? 'bg-blue-100 text-blue-700' :
                                                    hire.status === 'pending_start' ? 'bg-yellow-100 text-yellow-700' :
                                                    hire.status === 'pending_end' ? 'bg-orange-100 text-orange-700' :
                                                    'bg-slate-100 text-slate-600'
                                                }`}>
                                                    {hire.status.replace('_', ' ')}
                                                </span>
                                                <span className="text-[10px] text-slate-400 font-bold flex items-center gap-1">
                                                    <FaHistory /> Hired {new Date(hire.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {hire.status === 'pending_start' && (
                                            <button onClick={() => handleConfirmStart(hire.id)} className="px-6 py-3 bg-yellow-500 text-white rounded-xl font-bold shadow-lg shadow-yellow-200 hover:bg-yellow-600 transition-all flex items-center gap-2">
                                                <FaPlay size={12} /> Confirm Arrival
                                            </button>
                                        )}
                                        {hire.status === 'active' && (
                                            <Link to={`/track-worker/${hire.id}`} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2">
                                                <FaMap size={12} /> Live Track
                                            </Link>
                                        )}
                                        {hire.status === 'pending_end' && (
                                            <button onClick={() => handleConfirmCompletion(hire.id)} className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all flex items-center gap-2">
                                                <FaCheckCircle size={14} /> Release Payment
                                            </button>
                                        )}
                                        {hire.status === 'completed' && (
                                            <div className="flex flex-col gap-2">
                                                <div className="px-6 py-2 bg-slate-100 text-slate-500 rounded-xl font-bold flex items-center justify-center gap-2 text-sm">
                                                    <FaFlagCheckered /> Completed
                                                </div>
                                                {!hire.rating ? (
                                                    <button 
                                                        onClick={() => {
                                                            setRatingTargets([{ 
                                                                workerId: hire.hired_worker?.id, 
                                                                workerName: hire.hired_worker?.name,
                                                                hireId: hire.id,
                                                                type: 'Direct_Hire'
                                                            }]);
                                                            setIsRatingModalOpen(true);
                                                        }}
                                                        className="px-6 py-2 bg-yellow-500 text-white rounded-xl font-bold hover:bg-yellow-600 transition-all text-sm flex items-center justify-center gap-2 shadow-lg shadow-yellow-100"
                                                    >
                                                        <FaStar /> Rate Professional
                                                    </button>
                                                ) : (
                                                    <div className="text-center">
                                                        <div className="flex justify-center text-yellow-400 gap-0.5">
                                                            {[...Array(hire.rating.rating)].map((_, i) => <FaStar key={i} size={10} />)}
                                                        </div>
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Feedback Given</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="bg-white p-12 rounded-[2rem] border-2 border-dashed border-slate-200 text-center">
                                <p className="text-slate-400 font-medium italic">No workers hired yet for this project.</p>
                            </div>
                        )}
                    </div>

                    {/* Pending Applicants (Only if spots remaining) */}
                    {hiredWorkers.length < details.required_workers && (
                        <div className="space-y-4 pt-4">
                            <h2 className="text-lg font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                                <FaUser className="text-blue-500" /> Interested Applicants ({applications.length})
                            </h2>
                            <div className="bg-white rounded-3xl border border-slate-100 divide-y divide-slate-50">
                                {applications.length > 0 ? (
                                    applications.map(app => (
                                        <div key={app.id} className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                            <div className="flex items-center gap-4">
                                                <img src={loadImage(app.applicant?.picture)} className="w-10 h-10 rounded-full object-cover" alt="User" />
                                                <div>
                                                    <p className="font-bold text-slate-800">{app.applicant?.name}</p>
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase">{new Date(app.createdAt).toLocaleDateString()}</p>
                                                </div>
                                            </div>
                                            <Link to={`/job/${job.id}`} className="text-xs font-black text-emerald-600 uppercase tracking-widest hover:underline">Review & Hire</Link>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-8 text-center text-slate-400 text-sm font-medium italic">Scanning for interested professionals...</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right: Job Context Card */}
                <div className="space-y-6">
                    <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                        <h3 className="text-sm font-black uppercase tracking-[0.2em] text-emerald-400 mb-6">Contract Overview</h3>
                        
                        <div className="space-y-6">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Budget Per Slot</p>
                                <p className="text-xl font-bold">৳{details.budget_per_worker}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Time Estimation</p>
                                <p className="text-xl font-bold">{details.estimated_duration_hours} Hours</p>
                            </div>
                            <div className="pt-6 border-t border-white/10">
                                <Link to={`/job/${job.id}`} className="flex items-center justify-center gap-2 w-full py-3 bg-white/10 hover:bg-white/20 rounded-xl font-bold transition-all text-sm">
                                    Edit Job Details
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-4 text-orange-600">
                            <FaExclamationCircle />
                            <p className="text-xs font-black uppercase tracking-widest">Escrow Notice</p>
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed font-medium">
                            Funds for all {details.required_workers} slots are currently held in secure escrow. Payments are only released individually as you confirm each worker's completion.
                        </p>
                    </div>
                </div>

            </main>
        <RatingModal 
            isOpen={isRatingModalOpen}
            onClose={() => setIsRatingModalOpen(false)}
            targets={ratingTargets}
            onSuccess={fetchJobDetails}
        />
        </div>
        );
        };

        export default JobManagementPage;
